import React, { useMemo, useState, useEffect } from 'react';
import { format as formatDate } from 'date-fns';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMechanicShare } from '@/contexts/MechanicShareContext';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthProvider';
import { useToast } from '@/hooks/use-toast';

type ServiceEntry = {
  id: string;
  car_id: string;
  service_date: string;
  shop_name: string | null;
  mechanic_name: string | null;
  location: string | null;
  contact_phone: string | null;
  work_done: Array<{ title: string; notes?: string }> | null;
  findings: string | null;
  suggested_next_fixes: Array<{ fix: string; due?: string }> | null;
  total_cents: number | null;
  status: 'draft' | 'submitted';
  media: Array<{ path: string; type: string }> | null;
  created_at: string;
};

type Task = {
  id: string;
  taskId?: string; // UUID for standalone tasks from tasks table
  serviceEntryId: string | null; // null for standalone tasks
  fix: string;
  due: string | null;
  serviceDate: string | null; // null for standalone tasks
  shopName: string | null;
  mechanicName: string | null; // For service entry tasks
  userId?: string; // For standalone tasks - user who created it
  userName?: string; // Display name of user who created standalone task
  createdAt?: string; // For standalone tasks
  status?: 'open' | 'scheduled' | 'done'; // Status for the task
  taskText?: string; // Original task text for service entry tasks (for matching)
};

const RecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { carId } = useParams<{ carId?: string }>();
  const { cars, deleteIssue } = useCar();
  const { formatCurrency, t } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();

  const targetCar = useMemo(() => {
    if (cars.length === 0) return undefined;
    if (carId) return cars.find(c => c.id === carId) || cars[0];
    return cars[0];
  }, [cars, carId]);

  // Memoize issues with stable dependency to prevent infinite loops
  const issues = useMemo(() => {
    return targetCar?.issues || [];
  }, [targetCar?.id, JSON.stringify(targetCar?.issues?.map((i: any) => i.id) || [])]);
  const [mediaCounts, setMediaCounts] = useState<Record<string, number>>({});
  const [deleteTarget, setDeleteTarget] = useState<{ carId: string; issueId: string } | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [urgencyFilter, setUrgencyFilter] = useState<string[]>([]); // 'Low' | 'Medium' | 'High'
  const [filterOpen, setFilterOpen] = useState(false);
  const [taskStatusFilter, setTaskStatusFilter] = useState<string[]>(['open']); // Default to 'open'
  const [serviceEntries, setServiceEntries] = useState<ServiceEntry[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingRepairs, setLoadingRepairs] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [deleteTaskTarget, setDeleteTaskTarget] = useState<Task | null>(null);
  const [issueToServiceEntryMap, setIssueToServiceEntryMap] = useState<Record<string, string>>({});

  const searchTab = useMemo(() => new URLSearchParams(location.search).get('tab') as 'issues'|'repairs'|'tasks'|null, [location.search]);
  const [activeTab, setActiveTab] = useState<'issues' | 'repairs' | 'tasks'>(searchTab || 'issues');
  useEffect(() => {
    setActiveTab(searchTab || 'issues');
  }, [searchTab]);

  // Load service entries (repairs) when repairs tab is active or car changes
  useEffect(() => {
    if (!targetCar?.id || activeTab !== 'repairs') {
      setServiceEntries([]);
      return;
    }

    const loadServiceEntries = async () => {
      setLoadingRepairs(true);
      try {
        const { data, error } = await supabase
          .from('service_entries')
          .select('*')
          .eq('car_id', targetCar.id)
          .order('service_date', { ascending: false });

        if (error) {
          console.error('Failed to load service entries:', error);
          setServiceEntries([]);
        } else {
          setServiceEntries((data || []) as ServiceEntry[]);
        }
      } catch (error) {
        console.error('Error loading service entries:', error);
        setServiceEntries([]);
      } finally {
        setLoadingRepairs(false);
      }
    };

    loadServiceEntries();
  }, [targetCar?.id, activeTab]);

  // Load tasks (from recommendations and standalone tasks) when tasks tab is active or car changes
  useEffect(() => {
    if (!targetCar?.id || activeTab !== 'tasks') {
      setTasks([]);
      return;
    }

    const loadTasks = async () => {
      if (!user?.id) {
        setTasks([]);
        setLoadingTasks(false);
        return;
      }

      setLoadingTasks(true);
      try {
        // Load tasks from service_entries.suggested_next_fixes
        const { data: serviceEntriesData, error: serviceEntriesError } = await supabase
          .from('service_entries')
          .select('id, service_date, shop_name, mechanic_name, suggested_next_fixes')
          .eq('car_id', targetCar.id)
          .not('suggested_next_fixes', 'is', null);

        // Load standalone tasks from tasks table (all statuses, not just pending)
        // Note: tasks table uses 'pending' instead of 'open', and supports 'scheduled', 'done', 'completed'
        const { data: standaloneTasksData, error: standaloneTasksError } = await supabase
          .from('tasks')
          .select('id, title, due_date, due_odometer, created_at, service_entry_id, source, status, user_id')
          .eq('car_id', targetCar.id)
          .in('status', ['pending', 'scheduled', 'done', 'completed']);

        // Load hidden service tasks to filter them out and get their status
        const { data: hiddenServiceTasksData, error: hiddenServiceTasksError } = await supabase
          .from('hidden_service_tasks')
          .select('service_entry_id, task_text, hidden, status')
          .eq('user_id', user.id)
          .eq('car_id', targetCar.id);

        if (serviceEntriesError) {
          console.error('Failed to load tasks from service entries:', serviceEntriesError);
        }
        if (standaloneTasksError) {
          console.error('Failed to load standalone tasks:', standaloneTasksError);
        }
        if (hiddenServiceTasksError) {
          console.error('Failed to load hidden service tasks:', hiddenServiceTasksError);
        }

        // Create a map of hidden tasks: key = `${service_entry_id}:${task_text}`, value = { hidden, status }
        const hiddenTasksMap = new Map<string, { hidden: boolean; status?: 'open' | 'scheduled' | 'done' }>();
        if (hiddenServiceTasksData) {
          hiddenServiceTasksData.forEach((h: any) => {
            const key = `${h.service_entry_id}:${h.task_text}`;
            hiddenTasksMap.set(key, { hidden: h.hidden, status: h.status });
          });
        }

        const allTasks: Task[] = [];

        // Extract tasks from suggested_next_fixes
        if (serviceEntriesData) {
          serviceEntriesData.forEach((entry: any) => {
            const suggestedFixes = entry.suggested_next_fixes || [];
            if (Array.isArray(suggestedFixes)) {
              suggestedFixes.forEach((rec: { fix?: string; due?: string }) => {
                if (rec.fix?.trim()) {
                  const taskText = rec.fix;
                  const hiddenKey = `${entry.id}:${taskText}`;
                  const hiddenInfo = hiddenTasksMap.get(hiddenKey);
                  
                  // Skip if task is hidden
                  if (hiddenInfo?.hidden) {
                    return;
                  }

                  // Map status: 'open' -> 'open', 'scheduled' -> 'scheduled', 'done' -> 'done', default 'open'
                  const status = hiddenInfo?.status || 'open';

                  allTasks.push({
                    id: `service-${entry.id}-${allTasks.length}`,
                    serviceEntryId: entry.id,
                    fix: taskText,
                    due: rec.due || null,
                    serviceDate: entry.service_date,
                    shopName: entry.shop_name,
                    mechanicName: entry.mechanic_name,
                    status: status as 'open' | 'scheduled' | 'done',
                    taskText: taskText,
                  });
                }
              });
            }
          });
        }

        // Add standalone tasks
        // First, collect unique user IDs to fetch display names
        const uniqueUserIds = new Set<string>();
        if (standaloneTasksData) {
          standaloneTasksData.forEach((task: any) => {
            if (task.user_id) {
              uniqueUserIds.add(task.user_id);
            }
          });
        }

        // Fetch user display names
        const userIdsArray = Array.from(uniqueUserIds);
        const userDisplayNamesMap = new Map<string, string>();
        if (userIdsArray.length > 0) {
          try {
            // Fetch user metadata from auth.users (display_name is in raw_user_meta_data)
            // Note: We can't directly query auth.users, so we'll need to fetch from public.users
            // and then get display_name from auth metadata via a function or we'll need to store it
            // For now, let's fetch what we can from public.users and use email as fallback
            const { data: usersData } = await supabase
              .from('users')
              .select('id, email')
              .in('id', userIdsArray);

            // For each user, try to get display_name from auth metadata
            // Since we can't query auth.users directly, we'll use a workaround:
            // We'll fetch the current user's metadata and for others, use email
            if (usersData) {
              for (const userData of usersData) {
                if (userData.id === user.id) {
                  // Current user - get from user object
                  const meta: any = user?.user_metadata || {};
                  const displayName = meta.display_name || meta.full_name || meta.name || 
                    (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null) ||
                    userData.email?.split('@')[0] || 'User';
                  userDisplayNamesMap.set(userData.id, displayName);
                } else {
                  // Other users - for now use email (we'll enhance this when car sharing is implemented)
                  const emailName = userData.email?.split('@')[0] || 'User';
                  userDisplayNamesMap.set(userData.id, emailName);
                }
              }
            }
          } catch (error) {
            console.error('Failed to fetch user display names:', error);
          }
        }

        if (standaloneTasksData) {
          standaloneTasksData.forEach((task: any) => {
            // Format due interval for display (without "Due in" prefix - that's added in UI)
            let dueDisplay: string | null = null;
            if (task.due_odometer) {
              dueDisplay = `${task.due_odometer} km`;
            } else if (task.due_date) {
              // Format date nicely
              const dueDate = new Date(task.due_date);
              const now = new Date();
              const monthsDiff = (dueDate.getFullYear() - now.getFullYear()) * 12 + (dueDate.getMonth() - now.getMonth());
              if (monthsDiff === 12 && dueDate.getMonth() === 0 && dueDate.getDate() === 1) {
                dueDisplay = t('records.dateFormats.nextYear') || 'next year';
              } else if (monthsDiff > 0 && monthsDiff <= 12) {
                dueDisplay = t('records.dateFormats.months', { count: monthsDiff });
              } else {
                dueDisplay = formatDate(dueDate, 'dd-LLL-yyyy');
              }
            }

            // Map task status: 'pending' -> 'open', 'completed'/'done' -> 'done', others -> 'open'
            let status: 'open' | 'scheduled' | 'done' = 'open';
            if (task.status === 'done' || task.status === 'completed') {
              status = 'done';
            } else if (task.status === 'scheduled') {
              status = 'scheduled';
            } else {
              status = 'open';
            }

            allTasks.push({
              id: `task-${task.id}`,
              taskId: task.id,
              serviceEntryId: task.service_entry_id || null,
              fix: task.title,
              due: dueDisplay,
              serviceDate: null,
              shopName: null,
              mechanicName: null,
              userId: task.user_id,
              userName: userDisplayNamesMap.get(task.user_id) || 'User',
              createdAt: task.created_at,
              status: status,
            });
          });
        }

        // Sort by date (newest first) - use serviceDate for service entry tasks, createdAt for standalone
        allTasks.sort((a, b) => {
          const dateA = a.serviceDate ? new Date(a.serviceDate).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
          const dateB = b.serviceDate ? new Date(b.serviceDate).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
          return dateB - dateA;
        });

        setTasks(allTasks);
      } catch (error) {
        console.error('Error loading tasks:', error);
        setTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    };

    loadTasks();
  }, [targetCar?.id, activeTab, user?.id]);

  // Detect which issues have media via media table
  useEffect(() => {
    const loadMediaCounts = async () => {
      const ids = issues.map((i: any) => i.id).filter(Boolean);
      if (ids.length === 0) {
        setMediaCounts({});
        return;
      }
      const { data, error } = await supabase
        .from('media')
        .select('symptom_id')
        .in('symptom_id', ids);
      if (error) {
        console.warn('Failed to check media for issues:', error);
        setMediaCounts({});
        return;
      }
      const counts: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        const id = row.symptom_id;
        counts[id] = (counts[id] || 0) + 1;
      });
      setMediaCounts(counts);
    };
    loadMediaCounts();
  }, [issues]);

  // Load service entries that resolved issues to create mapping
  useEffect(() => {
    if (!targetCar?.id || activeTab !== 'issues') {
      setIssueToServiceEntryMap({});
      return;
    }

    const loadIssueToServiceEntryMapping = async () => {
      try {
        // Get all resolved issue IDs
        const resolvedIssueIds = issues
          .filter((i: any) => i.status === 'resolved')
          .map((i: any) => i.id)
          .filter(Boolean);

        if (resolvedIssueIds.length === 0) {
          setIssueToServiceEntryMap({});
          return;
        }

        // Query service entries that resolved these issues
        const { data, error } = await supabase
          .from('service_entries')
          .select('id, issues_resolved_ids')
          .eq('car_id', targetCar.id)
          .not('issues_resolved_ids', 'is', null);

        if (error) {
          console.warn('Failed to load service entries for issue mapping:', error);
          setIssueToServiceEntryMap({});
          return;
        }

        // Create mapping: issueId -> serviceEntryId
        const mapping: Record<string, string> = {};
        if (data) {
          data.forEach((entry: any) => {
            const resolvedIds = entry.issues_resolved_ids || [];
            if (Array.isArray(resolvedIds)) {
              resolvedIds.forEach((issueId: string) => {
                if (resolvedIssueIds.includes(issueId)) {
                  // If multiple service entries resolved the same issue, use the most recent one
                  // (entries are already sorted by service_date desc in the query)
                  if (!mapping[issueId]) {
                    mapping[issueId] = entry.id;
                  }
                }
              });
            }
          });
        }

        setIssueToServiceEntryMap(mapping);
      } catch (error) {
        console.error('Error loading issue to service entry mapping:', error);
        setIssueToServiceEntryMap({});
      }
    };

    loadIssueToServiceEntryMapping();
  }, [targetCar?.id, activeTab, issues]);

  const toggleStatus = (status: string) => {
    setStatusFilter(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  const toggleUrgency = (label: string) => {
    setUrgencyFilter(prev => prev.includes(label) ? prev.filter(s => s !== label) : [...prev, label]);
  };

  const statusColor: Record<string, string> = useMemo(() => ({
    draft: '#CBCBCB',
    open: '#e7fe52',
    recurred: '#bfd0fd',
    resolved: '#a3f88f',
  }), []);

  // Centralized status style mapping (bg/text/border)
  const statusStyles = useMemo(() => ({
    draft: { bg: '#ffffff', text: '#6b7280', border: '#CBCBCB', needsBorder: true },
    open: { bg: '#eff2fc', text: '#7c9ffa', border: '#7c9ffa' },
    recurred: { bg: '#defbfc', text: '#007a85', border: '#00e0e8' },
    resolved: { bg: '#e9fce9', text: '#2d7a42', border: '#2d7a42' },
  } as Record<string, { bg: string; text: string; border: string; needsBorder?: boolean }>), []);

  // Task status style mapping (bg/text/border)
  const taskStatusStyles = useMemo(() => ({
    open: { bg: '#eff2fc', text: '#7c9ffa', border: '#7c9ffa' },
    scheduled: { bg: '#fff4e6', text: '#f57c00', border: '#f57c00' },
    done: { bg: '#e9fce9', text: '#2d7a42', border: '#2d7a42' },
  } as Record<string, { bg: string; text: string; border: string }>), []);

  const urgencyOf = (i: any): 'Low' | 'Medium' | 'High' => {
    if (i.urgency_label) return i.urgency_label as 'Low' | 'Medium' | 'High';
    const raw = i.urgency !== undefined ? i.urgency : i.urgencyLevel;
    const u = Number(raw);
    if (Number.isFinite(u)) {
      if (u <= 2) return 'Low';
      if (u === 3) return 'Medium';
      return 'High';
    }
    return 'Medium';
  };

  const filteredIssues = useMemo(() => {
    let list = issues;
    if (statusFilter.length > 0) {
      list = list.filter(i => statusFilter.includes(i.status));
    }
    if (urgencyFilter.length > 0) {
      list = list.filter(i => urgencyFilter.includes(urgencyOf(i)));
    }
    // Sort newest to oldest by date
    return [...list].sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [issues, statusFilter, urgencyFilter]);

  const filteredTasks = useMemo(() => {
    let list = tasks;
    if (taskStatusFilter.length > 0) {
      list = list.filter(t => taskStatusFilter.includes(t.status || 'open'));
    }
    // Sort by date (newest first) - use serviceDate for service entry tasks, createdAt for standalone
    return [...list].sort((a, b) => {
      const dateA = a.serviceDate ? new Date(a.serviceDate).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      const dateB = b.serviceDate ? new Date(b.serviceDate).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      return dateB - dateA;
    });
  }, [tasks, taskStatusFilter]);

  const toggleTaskStatus = (status: string) => {
    setTaskStatusFilter(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  // Actions
  const { createShareLink } = useMechanicShare();

  const handleEdit = (issueId: string) => {
    navigate(`/symptom-summary/${targetCar?.id}/${issueId}?tab=issues&carId=${targetCar?.id || ''}`);
  };

  const handleShare = async () => {
    if (!targetCar?.id) return;
    const link = createShareLink(targetCar.id, true, 48);
    const url = `${window.location.origin}/share/${link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: t('records.share.linkCopied') || 'Link copied',
        description: t('records.share.linkCopiedDesc') || 'Share link copied to clipboard',
      });
    } catch (e) {
      toast({
        title: t('records.share.shareLink') || 'Share link',
        description: url,
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { data, error } = await supabase.functions.invoke('delete-symptom', {
        body: { symptomId: deleteTarget.issueId },
      });
      if (error) throw error;
    } catch (e) {
      console.error('Failed to delete via function:', e);
      return; // don't update local if backend failed
    }
    deleteIssue(deleteTarget.carId, deleteTarget.issueId);
    setDeleteTarget(null);
    setSuccessOpen(true);
  };

  const handleDeleteTask = async (task: Task) => {
    if (!user?.id || !targetCar?.id) return;

    try {
      if (task.taskId) {
        // Standalone task - delete from tasks table
        // RLS policy allows deletion if user has access to the car (via user_cars)
        const { error } = await supabase
          .from('tasks')
          .delete()
          .eq('id', task.taskId);

        if (error) throw error;
      } else if (task.serviceEntryId && task.taskText) {
        // Service entry task - mark as hidden in hidden_service_tasks table
        const { error: upsertError } = await supabase
          .from('hidden_service_tasks')
          .upsert({
            user_id: user.id,
            car_id: targetCar.id,
            service_entry_id: task.serviceEntryId,
            task_text: task.taskText,
            hidden: true,
            status: task.status || 'open',
          }, {
            onConflict: 'user_id,service_entry_id,task_text',
          });

        if (upsertError) throw upsertError;
      }

      // Remove from local state immediately for better UX
      setTasks(prev => prev.filter(t => t.id !== task.id));

      toast({
        title: t('records.tasks.deletedTitle') || 'Task deleted',
        description: t('records.tasks.deletedDesc') || 'The task has been removed.',
      });
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast({
        title: t('common.error') || 'Error',
        description: t('records.tasks.deleteError') || 'Failed to delete task. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateTaskStatus = async (task: Task, newStatus: 'open' | 'scheduled' | 'done') => {
    if (!user?.id || !targetCar?.id) return;

    try {
      if (task.taskId) {
        // Standalone task - update status in tasks table
        // Map our status to task table status: 'open' -> 'pending', 'scheduled' -> 'scheduled', 'done' -> 'done'
        const taskStatus = newStatus === 'open' ? 'pending' : newStatus === 'scheduled' ? 'scheduled' : 'done';
        
        const { error } = await supabase
          .from('tasks')
          .update({ 
            status: taskStatus,
            updated_at: new Date().toISOString(),
            ...(newStatus === 'done' ? { completed_at: new Date().toISOString() } : {}),
          })
          .eq('id', task.taskId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else if (task.serviceEntryId && task.taskText) {
        // Service entry task - update status in hidden_service_tasks table
        const { error: upsertError } = await supabase
          .from('hidden_service_tasks')
          .upsert({
            user_id: user.id,
            car_id: targetCar.id,
            service_entry_id: task.serviceEntryId,
            task_text: task.taskText,
            hidden: false, // Not hidden, just updating status
            status: newStatus,
          }, {
            onConflict: 'user_id,service_entry_id,task_text',
          });

        if (upsertError) throw upsertError;
      }

      // Update local state
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, status: newStatus } : t
      ));
    } catch (error) {
      console.error('Failed to update task status:', error);
      toast({
        title: t('common.error') || 'Error',
        description: t('records.tasks.updateStatusError') || 'Failed to update task status. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
    <PageLayout title={t('records.title') || 'Records'} showBackButton onBack={() => navigate(-1)}>
      <div className="space-y-4 px-2 pb-16 max-w-full overflow-x-hidden">
        {/* Top tabs */}
        <div className="w-full">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
            <TabsList className="w-full grid grid-cols-3 p-0 m-0 rounded-lg bg-gray-100 gap-1">
              <TabsTrigger value="issues" className="w-full h-9 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow flex items-center justify-center">
                {t('records.tabs.issues') || 'Issues'}
              </TabsTrigger>
              <TabsTrigger value="repairs" className="w-full h-9 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow flex items-center justify-center">
                {t('records.tabs.repairs') || 'Repairs'}
              </TabsTrigger>
              <TabsTrigger value="tasks" className="w-full h-9 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow flex items-center justify-center">
                {t('records.tabs.tasks') || 'Tasks'}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex items-center justify-between gap-2 mt-2">
          <div
            className="inline-flex items-center justify-center gap-2"
            style={{
              fontFamily: 'Lato',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: '22px',
              letterSpacing: '0.3px',
              color: '#000',
            }}
          >
            {activeTab === 'issues'
              ? (t('records.headers.allIssues') || 'All issues')
              : activeTab === 'repairs'
                ? (t('records.headers.allRepairs') || 'All repairs')
                : (t('records.headers.tasks') || 'Tasks')}
            {activeTab === 'issues' && (
              <Badge variant="outline" className="ml-2">{issues.length}</Badge>
            )}
            {activeTab === 'repairs' && (
              <Badge variant="outline" className="ml-2">{serviceEntries.length}</Badge>
            )}
            {activeTab === 'tasks' && (
              <Badge variant="outline" className="ml-2">{filteredTasks.length}</Badge>
            )}
          </div>
          {cars.length > 0 && (
            <div className="w-1/2">
              <Select
                value={targetCar?.id}
                onValueChange={(value) => navigate(`/records/${value}?tab=${activeTab}`)}
              >
                <SelectTrigger className="w-full bg-white shadow rounded-xl">
                  <SelectValue placeholder={t('records.chooseCar') || 'Choose a car'} />
                </SelectTrigger>
                <SelectContent>
                  {cars.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nickname || `${c.brand} ${c.model}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {activeTab === 'issues' && (
        <div className="mt-1 flex items-center justify-between gap-2">
          <p
            className="m-0 italic"
            style={{
              color: '#000',
              fontFamily: 'Lato',
              fontSize: 16,
              fontStyle: 'italic',
              fontWeight: 400,
              lineHeight: '28px',
              letterSpacing: 0,
            }}
          >
            {t('records.motto') || 'Remember, every issue has a solution!'}
          </p>
        
        {/* Status filter (popover) */}
        <div className="flex items-center gap-2">
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                className="flex items-center justify-center w-8 h-8 border border-input bg-white shadow rounded-xl"
                aria-label={t('records.filters.aria') || 'Filter'}
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="2" y="3" width="12" height="2" rx="1" fill="currentColor" />
                  <rect x="2" y="7" width="8" height="2" rx="1" fill="currentColor" />
                  <rect x="2" y="11" width="4" height="2" rx="1" fill="currentColor" />
                </svg>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3">
              <div className="text-sm font-medium mb-2">{t('records.filters.byStatus') || 'Filter by status'}</div>
              <div className="space-y-2">
                {['draft', 'open', 'recurred', 'resolved'].map((s) => (
                  <label key={s} className="flex items-center justify-between text-sm capitalize">
                    <span className="mr-3">
                      {(() => {
                        const status = s as string;
                        // Palette aligned with example
                        const bg = status === 'open' ? '#eff2fc' : status === 'resolved' ? '#e9fce9' : status === 'recurred' ? '#defbfc' : '#ffffff';
                        const fg = status === 'open' ? '#7c9ffa' : status === 'resolved' ? '#2d7a42' : status === 'recurred' ? '#007a85' : '#6b7280';
                        const border = status === 'draft' ? '1px solid #D1D5DB' : 'none';
                        return (
                          <Badge className="px-2 py-0.5 text-xs font-medium" style={{ background: bg, color: fg, fontFamily: 'Lato', border }}>
                            {t(`records.status.${status}`) || status}
                          </Badge>
                        );
                      })()}
                    </span>
                    <Checkbox
                      className="h-4 w-4 rounded-[4px] border-gray-300 bg-white data-[state=checked]:bg-[#7097fa] data-[state=checked]:border-[#7097fa] data-[state=checked]:text-white"
                      checked={statusFilter.includes(s)}
                      onCheckedChange={() => toggleStatus(s)}
                    />
                  </label>
                ))}
              </div>

              <div className="text-sm font-medium mt-4 mb-2">{t('records.filters.byUrgency') || 'Filter by urgency'}</div>
              <div className="space-y-2">
                {['Low', 'Medium', 'High'].map((u) => (
                  <label key={u} className="flex items-center justify-between text-sm">
                    <span className="mr-3 flex items-center gap-2 px-2 py-0.5" style={{ fontFamily: 'Lato' }}>
                      <span className={`w-2.5 h-2.5 rounded-full ${u === 'Low' ? 'bg-[#4caf50]' : u === 'Medium' ? 'bg-[#fbc02d]' : 'bg-[#f44336]'}`}></span>
                      <span className="text-gray-700 text-xs font-medium" style={{ fontFamily: 'Lato' }}>
                        {t(`records.urgency.${u.toLowerCase()}`) || u}
                      </span>
                    </span>
                    <Checkbox
                      className="h-4 w-4 rounded-[4px] border-gray-300 bg-white data-[state=checked]:bg-[#7097fa] data-[state=checked]:border-[#7097fa] data-[state=checked]:text-white"
                      checked={urgencyFilter.includes(u)}
                      onCheckedChange={() => toggleUrgency(u)}
                    />
                  </label>
                ))}
              </div>
              <div className="flex justify-between mt-3">
                <Button variant="link" size="sm" className="font-medium" onClick={() => { setStatusFilter([]); setUrgencyFilter([]); }} type="button">
                  {t('records.filters.clear') || 'Clear'}
                </Button>
                <Button size="sm" className="font-medium" type="button" onClick={() => setFilterOpen(false)}>
                  {t('records.filters.done') || 'Done'}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          </div>
        </div>
        )}

        {activeTab === 'issues' && (filteredIssues.length === 0 ? (
          <div className="text-sm text-gray-500">{t('records.issues.empty') || 'No issues found for this car.'}</div>
        ) : (
          <Accordion type="single" collapsible className="w-full max-w-full overflow-x-hidden">
            {filteredIssues.map((issue) => (
              <AccordionItem key={issue.id} value={issue.id} className="mb-2 border-b-0">
                <div
                  className="rounded-2xl bg-white shadow-md border-l-4 overflow-hidden"
                  style={{
                    borderColor: (statusStyles[(issue as any).status] || statusStyles.draft).border,
                  }}
                >
                  <AccordionTrigger className="group px-3 py-3 w-full max-w-full overflow-hidden [&>svg]:hidden">
                    <div className="flex w-full items-center gap-3">
                      <div className="min-w-0 text-left flex-1 pr-0 overflow-hidden">
                        <div className="truncate flex items-center gap-2">
                          <span className="truncate text-sm" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: 14 }}>
                            {issue.title || (t('records.issues.issueFallback') || 'Issue')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          <div className="flex items-center min-w-0 gap-2">
                            <span className="truncate">{formatDate(new Date(issue.date), 'dd-LLL-yyyy')}</span>
                            {(() => {
                              const u = urgencyOf(issue);
                              const dot = u === 'Low' ? 'bg-[#4caf50]' : u === 'Medium' ? 'bg-[#fbc02d]' : 'bg-[#f44336]';
                              return (
                                <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium" style={{ fontFamily: 'Lato', color: '#374151' }}>
                                  <span className={`w-2.5 h-2.5 rounded-full ${dot}`}></span>
                                  {t(`records.urgency.${u.toLowerCase()}`) || u}
                                </span>
                              );
                            })()}
                            {(mediaCounts[issue.id] || 0) > 0 && (
                              <span className="flex items-center gap-1">
                                <img src="/icons/Image.png" alt={t('records.ariaLabels.hasImages') || 'Has images'} className="w-3.5 h-3.5" />
                                <span>{mediaCounts[issue.id]}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 w-20 relative">
                        {/* Status pill vertically centered, keep same X position */}
                        {(() => {
                          const status = (issue as any).status as string;
                          const ss = statusStyles[status] || statusStyles.draft;
                          return (
                            <span className="absolute right-5 top-1/2 -translate-y-1/2 px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap" style={{ background: ss.bg, color: ss.text, fontFamily: 'Lato', border: ss.needsBorder ? '1px solid #D1D5DB' : 'none' }}>
                              {t(`records.status.${status}`) || status}
                            </span>
                          );
                        })()}
                        <svg className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-4">
                    <div className="space-y-3">
                      {issue.aiSummary && (
                        <div className="mt-2 rounded-xl p-3 bg-[#eff2fc]">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">{t('records.issueAccordion.problem') || 'Problem'}</div>
                          </div>
                          <div className="text-sm text-gray-800 mt-1">{issue.aiSummary}</div>
                        </div>
                      )}
                      {issue.possibleCauses && issue.possibleCauses.length > 0 && (
                        <div className="mt-2">
                          <div className="text-sm font-semibold">{t('records.issueAccordion.possibleCauses') || 'Possible causes'}</div>
                          <ul className="list-disc pl-5 text-sm text-gray-800 mt-1">
                            {issue.possibleCauses.map((c: any, idx: number) => (
                              <li key={idx}>{typeof c === 'string' ? c : c?.label || JSON.stringify(c)}</li>
                            ))}
                          </ul>
                          {(mediaCounts[issue.id] || 0) > 0 && (
                            <div className="mt-2">
                              <div className="text-sm font-semibold">
                                {t('records.issueAccordion.addedMediaFiles', { count: mediaCounts[issue.id] }) || `Added media files (${mediaCounts[issue.id]})`}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Associated repair section for resolved issues */}
                      {(issue as any).status === 'resolved' && issueToServiceEntryMap[issue.id] && (
                        <div className="mt-2">
                          <div className="text-sm font-semibold text-gray-700">
                            {t('records.issueAccordion.associatedRepair') || 'Associated repair:'}{' '}
                            <button
                              type="button"
                              onClick={() => navigate(`/services/view/${issueToServiceEntryMap[issue.id]}?tab=repairs&carId=${targetCar?.id || ''}`)}
                              className="text-[#7c9ffa] hover:underline font-medium"
                            >
                              {t('records.issueAccordion.viewRecord') || 'View record'}
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-3 pt-2 w-full flex-nowrap">
                        <Button
                          variant="destructive"
                          className="flex-1 min-w-0 w-full py-2 rounded-xl text-sm font-medium"
                          onClick={() => {
                            if (targetCar?.id) {
                              setDeleteTarget({ carId: targetCar.id, issueId: issue.id });
                            }
                          }}
                          type="button"
                        >
                          {t('records.actions.delete') || 'Delete'}
                        </Button>
                        <Button
                          variant="secondary"
                          className="flex-1 min-w-0 w-full py-2 rounded-xl text-sm font-medium"
                          onClick={handleShare}
                          type="button"
                        >
                          {t('records.actions.share') || 'Share'}
                        </Button>
                        <Button
                          className="flex-1 min-w-0 w-full py-2 rounded-xl bg-[#e7fe52] text-black text-sm font-medium hover:bg-[#e7fe52]/90"
                          onClick={() => handleEdit(issue.id)}
                          type="button"
                        >
                          {t('records.issues.viewFull') || 'View Full'}
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </div>
              </AccordionItem>
            ))}
          </Accordion>
        ))}

        {activeTab === 'repairs' && (
          <div className="w-full max-w-full overflow-x-hidden">
            {loadingRepairs ? (
              <div className="text-sm text-gray-500">{t('records.repairs.loading') || 'Loading repairs...'}</div>
            ) : serviceEntries.length === 0 ? (
              <div className="text-sm text-gray-500">{t('records.repairs.empty') || 'No repairs found for this car.'}</div>
            ) : (
              <Accordion type="single" collapsible className="w-full max-w-full overflow-x-hidden">
                {serviceEntries.map((entry) => {
                  const workItems = Array.isArray(entry.work_done) ? entry.work_done : [];
                  const mediaCount = Array.isArray(entry.media) ? entry.media.length : 0;
                  const totalCost = entry.total_cents ? entry.total_cents / 100 : null;
                  
                  return (
                    <AccordionItem key={entry.id} value={entry.id} className="mb-2 border-b-0">
                      <div
                        className="rounded-2xl bg-white shadow-md border-l-4 overflow-hidden"
                        style={{
                          borderColor: entry.status === 'submitted' ? '#2d7a42' : '#CBCBCB',
                        }}
                      >
                        <AccordionTrigger className="group px-3 py-3 w-full max-w-full overflow-hidden [&>svg]:hidden">
                          <div className="flex w-full items-center gap-3">
                            <div className="min-w-0 text-left flex-1 pr-0 overflow-hidden">
                              <div className="truncate flex items-center gap-2">
                                <span className="truncate text-sm" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: 14 }}>
                                  {(() => {
                                    const parts: string[] = [];
                                    if (entry.shop_name) parts.push(entry.shop_name);
                                    if (entry.mechanic_name) parts.push(entry.mechanic_name);
                                    return parts.length > 0 ? parts.join(' | ') : (entry.location || 'Service Entry');
                                  })()}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                <div className="flex items-center min-w-0 gap-2">
                                  <span className="truncate">{formatDate(new Date(entry.service_date), 'dd-LLL-yyyy')}</span>
                                  {workItems.length > 0 && (
                                    <span className="text-[10px] font-medium" style={{ fontFamily: 'Lato', color: '#374151' }}>
                                  {workItems.length}{' '}
                                  {workItems.length === 1
                                    ? (t('records.repairs.workItem') || 'work item')
                                    : (t('records.repairs.workItems') || 'work items')}
                                    </span>
                                  )}
                                  {mediaCount > 0 && (
                                    <span className="flex items-center gap-1">
                                      <img src="/icons/Image.png" alt={t('records.ariaLabels.hasImages') || 'Has images'} className="w-3.5 h-3.5" />
                                      <span>{mediaCount}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="shrink-0 w-20 relative">
                              {totalCost !== null && (
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap" style={{ background: '#e9fce9', color: '#2d7a42', fontFamily: 'Lato' }}>
                                  {formatCurrency(totalCost)}
                                </span>
                              )}
                              <svg className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-3 pb-4">
                          <div className="space-y-3">
                            {workItems.length > 0 && (
                              <div className="mt-2 rounded-lg p-2" style={{ backgroundColor: '#f3f4f6' }}>
                                <div className="text-sm font-semibold mb-1">{t('records.repairs.workDone') || 'Work Done'}</div>
                                <ul className="list-disc pl-4 text-sm text-gray-800 space-y-0.5">
                                  {workItems.map((item, idx) => (
                                    <li key={idx}>
                                      <span className="font-medium">{item.title}</span>
                                      {item.notes && <span className="text-gray-600"> - {item.notes}</span>}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {entry.findings && (
                              <div className="mt-2">
                                <div className="text-sm font-semibold text-gray-700">{t('records.repairs.findings') || 'Findings'}</div>
                                <div className="text-sm text-gray-600 mt-1">{entry.findings}</div>
                              </div>
                            )}
                            {entry.suggested_next_fixes && Array.isArray(entry.suggested_next_fixes) && entry.suggested_next_fixes.length > 0 && (
                              <div className="mt-2">
                                <div className="text-sm font-semibold text-gray-700">{t('records.repairs.suggestedNextFixes') || 'Suggested Next Fixes'}</div>
                                <ul className="list-disc pl-5 text-sm text-gray-600 mt-1 space-y-1">
                                  {entry.suggested_next_fixes.map((rec: any, idx: number) => (
                                    <li key={idx}>
                                      <span>{rec.fix || ''}</span>
                                      {rec.due && <span className="text-gray-500"> - {rec.due}</span>}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <div className="flex gap-3 pt-2 w-full flex-nowrap">
                              <Button
                                className="flex-1 min-w-0 w-full py-2 rounded-xl bg-[#e7fe52] text-black text-sm font-medium hover:bg-[#e7fe52]/90"
                                onClick={() => navigate(`/services/view/${entry.id}?tab=repairs&carId=${entry.car_id}`)}
                                type="button"
                              >
                                {t('records.repairs.viewFull') || 'View Full'}
                              </Button>
                            </div>
                          </div>
                        </AccordionContent>
                      </div>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="w-full max-w-full overflow-x-hidden">
            {/* Task status filter row */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {['open', 'scheduled', 'done'].map((status) => {
                const isSelected = taskStatusFilter.includes(status);
                const ss = taskStatusStyles[status] || taskStatusStyles.open;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => toggleTaskStatus(status)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all ${
                      isSelected ? 'shadow-sm' : 'opacity-60'
                    }`}
                    style={{
                      background: isSelected ? ss.bg : '#f3f4f6',
                      color: isSelected ? ss.text : '#6b7280',
                      fontFamily: 'Lato',
                      border: isSelected ? `1px solid ${ss.border}` : '1px solid #e5e7eb',
                    }}
                  >
                    {status === 'open'
                      ? (t('records.tasks.status.open') || 'Open')
                      : status === 'scheduled'
                        ? (t('records.tasks.status.scheduled') || 'Scheduled')
                        : (t('records.tasks.status.done') || 'Done')}
                  </button>
                );
              })}
            </div>

            {loadingTasks ? (
              <div className="text-sm text-gray-500">{t('records.tasks.loading') || 'Loading tasks...'}</div>
            ) : filteredTasks.length === 0 ? (
              <div className="text-sm text-gray-500">{t('records.tasks.empty') || 'No tasks available.'}</div>
            ) : (
              <Accordion type="single" collapsible className="w-full max-w-full overflow-x-hidden">
                {filteredTasks.map((task) => (
                  <AccordionItem key={task.id} value={task.id} className="mb-2 border-b-0">
                    <div 
                      className="rounded-2xl bg-white shadow-md border-l-4 overflow-hidden" 
                      style={{ 
                        borderColor: (() => {
                          const status = task.status || 'open';
                          const ss = taskStatusStyles[status] || taskStatusStyles.open;
                          return ss.border;
                        })()
                      }}
                    >
                      <AccordionTrigger className="group px-3 py-3 w-full max-w-full overflow-hidden [&>svg]:hidden">
                        <div className="flex w-full items-center gap-3">
                          <div className="min-w-0 text-left flex-1 pr-0 overflow-hidden">
                            <div className="truncate flex items-center gap-2">
                              <span className="truncate text-sm" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: 14 }}>
                                {task.fix}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              <div className="flex items-center min-w-0 gap-2">
                                {task.due && (
                                  <span className="text-[10px] font-medium" style={{ fontFamily: 'Lato', color: '#374151' }}>
                                    {t('records.tasks.labels.dueIn') || 'Due in'}: {task.due}
                                  </span>
                                )}
                                {/* Separator and issuer information */}
                                {task.due && ((task.serviceEntryId && (task.shopName || task.mechanicName)) || (!task.serviceEntryId && task.userName)) && (
                                  <span className="text-[10px] font-medium" style={{ fontFamily: 'Lato', color: '#374151' }}>
                                    |
                                  </span>
                                )}
                                {/* For service entry tasks: show shop name or mechanic name */}
                                {task.serviceEntryId && (
                                  <>
                                    {task.shopName ? (
                                      <span className="text-[10px] font-medium" style={{ fontFamily: 'Lato', color: '#374151' }}>
                                        {task.shopName}
                                      </span>
                                    ) : task.mechanicName ? (
                                      <span className="text-[10px] font-medium" style={{ fontFamily: 'Lato', color: '#374151' }}>
                                        {task.mechanicName}
                                      </span>
                                    ) : null}
                                  </>
                                )}
                                {/* For standalone tasks: show user name who added it */}
                                {!task.serviceEntryId && task.userName && (
                                  <span className="text-[10px] font-medium" style={{ fontFamily: 'Lato', color: '#374151' }}>
                                    {t('carSharing.addedBy') || 'Added by'} {task.userName}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="shrink-0 w-20 relative">
                            {/* Status pill vertically centered, keep same X position */}
                            {(() => {
                              const status = task.status || 'open';
                              const ss = taskStatusStyles[status] || taskStatusStyles.open;
                              return (
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap" style={{ background: ss.bg, color: ss.text, fontFamily: 'Lato' }}>
                                  {status === 'open'
                                    ? (t('records.tasks.status.open') || 'Open')
                                    : status === 'scheduled'
                                      ? (t('records.tasks.status.scheduled') || 'Scheduled')
                                      : status === 'done'
                                        ? (t('records.tasks.status.done') || 'Done')
                                        : String(status)}
                                </span>
                              );
                            })()}
                            <svg className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-4">
                        <div className="space-y-3">
                          <div className="text-sm">
                            <span className="font-semibold">{t('records.tasks.labels.task') || 'Task'}: </span>
                            <span className="text-gray-800">{task.fix}</span>
                          </div>
                          
                          {/* Status dropdown */}
                          <div className="text-sm">
                            <span className="font-semibold mb-2 block">{t('records.tasks.labels.status') || 'Status'}: </span>
                            <Select
                              value={task.status || 'open'}
                              onValueChange={(value) => handleUpdateTaskStatus(task, value as 'open' | 'scheduled' | 'done')}
                            >
                              <SelectTrigger className="w-full bg-white border border-gray-300 rounded-lg">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">{t('records.tasks.status.open') || 'Open'}</SelectItem>
                                <SelectItem value="scheduled">{t('records.tasks.status.scheduled') || 'Scheduled'}</SelectItem>
                                <SelectItem value="done">{t('records.tasks.status.done') || 'Done'}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {task.due && (
                            <div className="text-sm">
                              <span className="font-semibold">{t('records.tasks.labels.due') || 'Due'}: </span>
                              <span className="text-gray-800">{task.due}</span>
                            </div>
                          )}
                          {task.serviceDate && (
                            <div className="text-sm">
                              <span className="font-semibold">{t('records.tasks.labels.fromServiceOn') || 'From service on'}: </span>
                              <span className="text-gray-800">{formatDate(new Date(task.serviceDate), 'dd-LLL-yyyy')}</span>
                            </div>
                          )}
                          {task.createdAt && !task.serviceDate && (
                            <div className="text-sm">
                              <span className="font-semibold">{t('records.tasks.labels.created') || 'Created'}: </span>
                              <span className="text-gray-800">{formatDate(new Date(task.createdAt), 'dd-LLL-yyyy')}</span>
                            </div>
                          )}
                          {task.shopName && (
                            <div className="text-sm">
                              <span className="font-semibold">{t('records.tasks.labels.shop') || 'Shop'}: </span>
                              <span className="text-gray-800">{task.shopName}</span>
                            </div>
                          )}
                          <div className="flex gap-3 pt-2 w-full flex-nowrap">
                            <Button
                              variant="destructive"
                              className="flex-1 min-w-0 w-full py-2 rounded-xl text-sm font-medium"
                              onClick={() => setDeleteTaskTarget(task)}
                              type="button"
                            >
                              {t('records.actions.delete') || 'Delete'}
                            </Button>
                            {task.serviceEntryId ? (
                              <Button
                                className="flex-1 min-w-0 w-full py-2 rounded-xl bg-[#e7fe52] text-black text-sm font-medium hover:bg-[#e7fe52]/90"
                                onClick={() => navigate(`/services/view/${task.serviceEntryId}?tab=tasks&carId=${targetCar?.id || ''}`)}
                                type="button"
                              >
                                {t('records.tasks.viewServiceEntry') || 'View Service Entry'}
                              </Button>
                            ) : (
                              <Button
                                className="flex-1 min-w-0 w-full py-2 rounded-xl bg-[#e7fe52] text-black text-sm font-medium hover:bg-[#e7fe52]/90"
                                onClick={() => navigate(`/records/${targetCar?.id}?tab=tasks`)}
                                type="button"
                              >
                                {t('actions.close') || 'Close'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </AccordionContent>
                    </div>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        )}
      </div>
    </PageLayout>
    {/* Delete confirmation dialog */}
    <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
      <AlertDialogContent>
        <div className="relative">
          <button
            aria-label={t('records.ariaLabels.close') || 'Close'}
            onClick={() => setDeleteTarget(null)}
            className="absolute right-2 top-2 rounded-md p-1 hover:bg-black/5"
            type="button"
          >
            ×
          </button>
        </div>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('records.dialogs.deleteIssue.title') || 'Delete Issue'}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('records.dialogs.deleteIssue.description') || 'Are you sure you want to delete this issue? This action cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-row justify-between items-center">
          <AlertDialogCancel>{t('actions.cancel') || 'Cancel'}</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDelete} className="bg-red-500 text-white hover:bg-red-600">{t('records.actions.delete') || 'Delete'}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    {/* Success dialog */}
    <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
      <DialogContent className="sm:max-w-[360px] p-6 text-center">
        <img src="/icons/Success.png" alt={t('records.ariaLabels.success') || 'Success'} className="w-12 h-12 mx-auto mb-3" />
        <div className="text-lg font-semibold">{t('records.success') || 'Success!'}</div>
      </DialogContent>
    </Dialog>
    {/* Delete task confirmation dialog */}
    <AlertDialog open={!!deleteTaskTarget} onOpenChange={(open) => { if (!open) setDeleteTaskTarget(null); }}>
      <AlertDialogContent>
        <div className="relative">
          <button
            aria-label={t('records.ariaLabels.close') || 'Close'}
            onClick={() => setDeleteTaskTarget(null)}
            className="absolute right-2 top-2 rounded-md p-1 hover:bg-black/5"
            type="button"
          >
            ×
          </button>
        </div>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('records.dialogs.deleteTask.title') || 'Delete Task'}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('records.dialogs.deleteTask.description') || 'Are you sure you want to delete this task? This action cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-row justify-between items-center">
          <AlertDialogCancel>{t('actions.cancel') || 'Cancel'}</AlertDialogCancel>
          <AlertDialogAction 
            onClick={() => {
              if (deleteTaskTarget) {
                handleDeleteTask(deleteTaskTarget);
                setDeleteTaskTarget(null);
              }
            }} 
            className="bg-red-500 text-white hover:bg-red-600"
          >
            {t('records.actions.delete') || 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default RecordsPage;


